import { useCallback, useEffect, useState } from 'react'
import { getTags } from '../api/gg'
import { GalleryPreview } from '../components/GalleryPreview'
import { InfiniteScrollSentinel } from '../components/InfiniteScrollSentinel'
import { Lightbox } from '../components/Lightbox'
import { MediaTile } from '../components/MediaTile'
import { NeonGridItem, NeonMediaGrid } from '../components/NeonMediaGrid'
import { TagPicker } from '../components/TagPicker'
import { useMediaFeed } from '../hooks/useMediaFeed'
import { buildFeedTags, formatSexualitySummary } from '../lib/feedPrefs'
import { useGame } from '../store/game'
import type { ContentKind, Tag } from '../types'

type Phase = 'pick' | 'loading' | 'view'

export function TiledMode() {
  const setSelection = useGame((s) => s.setSelection)
  const feedPrefs = useGame((s) => s.feedPrefs)

  const [phase, setPhase] = useState<Phase>('pick')
  const [tags, setTags] = useState<Tag[]>([])
  const [tagLabels, setTagLabels] = useState<string[]>([])
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [sweepKey, setSweepKey] = useState(0)

  const { items, progress, loading, loadingMore, hasMore, error, startBrowse, loadMore, cancel, setError } =
    useMediaFeed()

  useEffect(() => {
    void getTags().then((t) => setTags(t.map((x) => ({ ...x, enabled: true, sort: 0 }))))
    return () => cancel()
  }, [cancel])

  const start = useCallback(
    async (tagIds: string[], kind: ContentKind) => {
      const chosen = tags.filter((t) => tagIds.includes(t.id))
      if (!chosen.length) return
      setSelection(tagIds, kind)
      setTagLabels(chosen.map((t) => t.label))
      setError(null)
      setPhase('loading')

      const feed = await startBrowse(buildFeedTags(chosen, feedPrefs), kind, 24)
      if (!feed) {
        setPhase('pick')
        return
      }
      setLightbox(null)
      setSweepKey((k) => k + 1)
      setPhase('view')
    },
    [tags, setSelection, feedPrefs, startBrowse, setError],
  )

  const cancelLoad = () => {
    cancel()
    setPhase('pick')
  }

  const changeTags = () => {
    cancel()
    setLightbox(null)
    setPhase('pick')
  }

  const onLoadMore = useCallback(() => loadMore(2), [loadMore])

  if (phase === 'pick')
    return (
      <main className="px-4 py-8">
        {error ? (
          <div className="mx-auto mb-4 max-w-2xl rounded-lg border-2 border-neon-red bg-neon-red/10 px-4 py-2 text-center font-bold text-neon-red">
            {error}
          </div>
        ) : null}
        <TagPicker tags={tags} onStart={start} ctaLabel="OPEN TILE GRID" />
      </main>
    )

  if (phase === 'loading' || loading)
    return (
      <GalleryPreview
        modeLabel="TILE MODE"
        tags={tagLabels}
        sexuality={formatSexualitySummary(feedPrefs)}
        fraction={progress.fraction}
        count={progress.count}
        note={progress.note}
        onCancel={cancelLoad}
      />
    )

  return (
    <main className="px-3 py-4">
      <div className="mx-auto mb-4 flex max-w-6xl items-center justify-between">
        <h2 className="goon-title comic-title-cyan text-3xl">{items.length} TILES</h2>
        <button onClick={changeTags} className="btn-ghost text-sm">Change tags</button>
      </div>

      <NeonMediaGrid itemCount={items.length} sweepKey={sweepKey} className="mx-auto max-w-6xl p-2">
        {items.map((item, i) => (
          <NeonGridItem key={item.id} index={i}>
            <MediaTile
              item={item}
              eager={i < 12}
              onClick={() => setLightbox(i)}
              className="aspect-square h-full w-full overflow-hidden rounded-xl border border-edge bg-surface shadow-cyber hover:border-pow"
            />
          </NeonGridItem>
        ))}
      </NeonMediaGrid>

      <InfiniteScrollSentinel
        onVisible={onLoadMore}
        disabled={!hasMore || loadingMore}
        className="mx-auto max-w-6xl"
      />

      {lightbox !== null ? (
        <Lightbox items={items} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
      ) : null}
    </main>
  )
}